/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { useKeyPress } from '@mpb-com/feather/hooks/use-key-press';
import React, { FunctionComponent, useRef } from 'react';
import styled from 'styled-components';

import {
    Tab,
    TabProps,
    TabVariant,
} from '@toucan/client/shared/components/tab-panel/components/tab-group/components/tab';

import { styles } from './tab-group.styles';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface TabGroupProps {
    /**
     * A list of tabs
     */
    tabs: TabProps[];

    /**
     * The currently active tab
     */
    activeIndex?: number;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * Handler for when a tab is selected.
     *
     * @param selectedIndex the index of the selected tab
     */
    onTabSelect?: (selectedIndex: number) => void;
    variant?: TabVariant;
}

const StyledTabGroup = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const TabGroup: FunctionComponent<TabGroupProps> = ({
    tabs,
    activeIndex = 0,
    onTabSelect,
    variant,
    ...otherProps
}: TabGroupProps) => {
    const tabGroupRef = useRef<HTMLDivElement>(null);

    useKeyPress(tabGroupRef.current, ['Right', 'ArrowRight'], (event: any) => {
        event.preventDefault();
        const hasFocus = document.activeElement === tabGroupRef.current;
        if (!hasFocus) {
            return;
        }
        onTabSelect && onTabSelect((activeIndex + 1) % tabs.length);
    });

    useKeyPress(tabGroupRef.current, ['Left', 'ArrowLeft'], (event: any) => {
        event.preventDefault();
        const hasFocus = document.activeElement === tabGroupRef.current;
        if (!hasFocus) {
            return;
        }
        onTabSelect &&
            onTabSelect((activeIndex - 1 + tabs.length) % tabs.length);
    });

    return (
        <StyledTabGroup
            tabIndex={0}
            ref={tabGroupRef}
            tabs={tabs}
            role="tablist"
            variant={variant}
            {...otherProps}
        >
            {tabs.map(({ children, ...tab }, index) => (
                <Tab
                    key={index}
                    {...tab}
                    className="tab"
                    onClick={() => onTabSelect && onTabSelect(index)}
                    onFocus={() => {
                        tabGroupRef?.current?.focus();
                    }}
                    active={activeIndex === index}
                    aria-selected={activeIndex === index}
                    tabIndex={-1}
                    variant={variant}
                >
                    {children}
                </Tab>
            ))}
        </StyledTabGroup>
    );
};
